/**
 * 🌟 Arkturian Settings Engine
 * Central state management with animation capabilities
 * 
 * Philosophy: The engine knows no UI. The UI knows no engine.
 * Both are connected through state, described by configuration.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  SettingsSchema, 
  SettingDefinition, 
  SettingsState, 
  SettingState, 
  SettingValue,
  AnimationState,
  AnimationConfig
} from '@/config/settings-schema';

interface SettingsStore {
  // Core State
  schema: SettingsSchema | null;
  settings: SettingsState;
  animations: Record<string, AnimationState>;
  
  // Actions
  loadSchema: (schema: SettingsSchema) => void;
  setValue: (key: string, value: SettingValue) => void;
  getValue: (key: string) => SettingValue | undefined;
  getAnimatedValue: (key: string) => number | SettingValue | undefined;
  
  // Animation Control
  startAnimation: (key: string, config?: Partial<AnimationConfig>) => void;
  stopAnimation: (key: string) => void;
  toggleAnimation: (key: string) => void;
  
  // Persistence
  persistSettings: () => void;
  loadPersistedSettings: () => void;
  
  // Utilities
  resetToDefaults: () => void;
  exportSettings: () => string;
  importSettings: (jsonString: string) => void;
}

/**
 * Animation calculation functions
 */
const calculateAnimatedValue = (
  baseValue: number,
  animation: AnimationState,
  currentTime: number
): number => {
  const { config, startTime } = animation;
  const elapsed = (currentTime - startTime) / 1000; // Convert to seconds
  
  let progress = (elapsed * config.frequency) % 1;
  
  // Apply easing and mode
  switch (config.mode) {
    case 'pingpong':
      progress = progress < 0.5 ? progress * 2 : (1 - progress) * 2;
      break;
    case 'once':
      progress = Math.min(progress, 1);
      break;
    case 'loop':
    default:
      // progress already calculated correctly for loop
      break;
  }
  
  // Apply easing function
  switch (config.easing) {
    case 'sine':
      progress = Math.sin(progress * Math.PI * 2);
      break;
    case 'cosine':
      progress = Math.cos(progress * Math.PI * 2);
      break;
    case 'ease-in-out':
      progress = 0.5 - 0.5 * Math.cos(progress * Math.PI);
      break;
    case 'linear':
    default:
      progress = progress * 2 - 1; // Convert to -1 to 1 range
      break;
  }
  
  return baseValue + (progress * config.amplitude);
};

/**
 * Initialize setting state from definition
 */
const initializeSettingState = (definition: SettingDefinition): SettingState => ({
  value: definition.type === 'group' || definition.type === 'section' 
    ? '' 
    : (definition as any).default,
  isAnimating: false,
  lastUpdated: Date.now(),
});

/**
 * Main Settings Store
 */
export const useSettingsStore = create<SettingsStore>()(
  subscribeWithSelector((set, get) => ({
    schema: null,
    settings: {},
    animations: {},

    loadSchema: (schema: SettingsSchema) => {
      const initialSettings: SettingsState = {};
      
      // Initialize all settings from schema
      const initializeSettings = (settings: Record<string, SettingDefinition>) => {
        Object.entries(settings).forEach(([key, definition]) => {
          initialSettings[key] = initializeSettingState(definition);
          
          // Handle nested settings (groups/sections)
          if (definition.type === 'group' || definition.type === 'section') {
            definition.children.forEach(child => {
              initialSettings[child.key] = initializeSettingState(child);
            });
          }
        });
      };
      
      initializeSettings(schema.settings);
      
      set({ 
        schema, 
        settings: initialSettings 
      });
      
      // Load persisted values
      get().loadPersistedSettings();
    },

    setValue: (key: string, value: SettingValue) => {
      set(state => ({
        settings: {
          ...state.settings,
          [key]: {
            ...state.settings[key],
            value,
            lastUpdated: Date.now(),
          }
        }
      }));
    },

    getValue: (key: string) => {
      const setting = get().settings[key];
      return setting?.value;
    },

    getAnimatedValue: (key: string) => {
      const { settings, animations } = get();
      const setting = settings[key];
      const animation = animations[key];
      
      if (!setting) return undefined;
      
      if (!animation?.isActive || typeof setting.value !== 'number') {
        return setting.value;
      }
      
      return calculateAnimatedValue(
        setting.value as number,
        animation,
        Date.now()
      );
    },

    startAnimation: (key: string, configOverride = {}) => {
      const { schema, settings } = get();
      if (!schema || !settings[key]) return;
      
      const settingDef = Object.values(schema.settings)
        .find(def => def.key === key || 
          (def.type === 'group' || def.type === 'section') && 
          def.children.some(child => child.key === key)
        );
      
      if (!settingDef?.animate || !settingDef.animationDefaults) return;
      
      const config: AnimationConfig = {
        ...settingDef.animationDefaults,
        ...configOverride,
        enabled: true,
      };
      
      set(state => ({
        settings: {
          ...state.settings,
          [key]: {
            ...state.settings[key],
            isAnimating: true,
          }
        },
        animations: {
          ...state.animations,
          [key]: {
            startTime: Date.now(),
            duration: Infinity, // Continuous animation
            baseValue: state.settings[key].value as number,
            config,
            isActive: true,
          }
        }
      }));
    },

    stopAnimation: (key: string) => {
      set(state => ({
        settings: {
          ...state.settings,
          [key]: {
            ...state.settings[key],
            isAnimating: false,
          }
        },
        animations: {
          ...state.animations,
          [key]: {
            ...state.animations[key],
            isActive: false,
          }
        }
      }));
    },

    toggleAnimation: (key: string) => {
      const { animations } = get();
      const animation = animations[key];
      
      if (animation?.isActive) {
        get().stopAnimation(key);
      } else {
        get().startAnimation(key);
      }
    },

    persistSettings: () => {
      const { settings } = get();
      try {
        localStorage.setItem('arkturian-settings', JSON.stringify(settings));
      } catch (error) {
        console.warn('Failed to persist settings:', error);
      }
    },

    loadPersistedSettings: () => {
      try {
        const stored = localStorage.getItem('arkturian-settings');
        if (stored) {
          const persistedSettings = JSON.parse(stored);
          set(state => ({
            settings: {
              ...state.settings,
              ...persistedSettings,
            }
          }));
        }
      } catch (error) {
        console.warn('Failed to load persisted settings:', error);
      }
    },

    resetToDefaults: () => {
      const { schema } = get();
      if (!schema) return;
      
      const defaultSettings: SettingsState = {};
      Object.entries(schema.settings).forEach(([key, definition]) => {
        defaultSettings[key] = initializeSettingState(definition);
      });
      
      set({ settings: defaultSettings });
    },

    exportSettings: () => {
      const { settings } = get();
      return JSON.stringify(settings, null, 2);
    },

    importSettings: (jsonString: string) => {
      try {
        const importedSettings = JSON.parse(jsonString);
        set(state => ({
          settings: {
            ...state.settings,
            ...importedSettings,
          }
        }));
      } catch (error) {
        console.error('Failed to import settings:', error);
      }
    },
  }))
);

/**
 * Hook for animated values - updates on animation frame
 */
export const useAnimatedValue = (key: string): number | SettingValue | undefined => {
  const getAnimatedValue = useSettingsStore(state => state.getAnimatedValue);
  const isAnimating = useSettingsStore(state => state.settings[key]?.isAnimating);
  
  const [animatedValue, setAnimatedValue] = React.useState(() => getAnimatedValue(key));
  
  React.useEffect(() => {
    if (!isAnimating) {
      setAnimatedValue(getAnimatedValue(key));
      return;
    }
    
    let animationFrame: number;
    
    const updateValue = () => {
      setAnimatedValue(getAnimatedValue(key));
      animationFrame = requestAnimationFrame(updateValue);
    };
    
    animationFrame = requestAnimationFrame(updateValue);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [key, isAnimating, getAnimatedValue]);
  
  return animatedValue;
};

// React import for hooks
import React from 'react'; 